import React from 'react';

import {
  createBillingDetailConfiguration,
  getBillingDetailConfiguration,
  updateBillingDetailConfiguration,
} from '~/src/dash/api/billing-detail/billing-detail-methods';
// api
import { IBillingDetailConfiguration } from '~/src/dash/api/billing-detail/billing-detail-models';
import { IPublisher } from '~/src/dash/api/console-api';
import { getPublishersSimplifiedV1 } from '~/src/dash/api/publisher';
import { EBool } from '~/src/dash/api/types/bool';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { Panel, Panels } from '~/src/dash/publicaUI';
// other
import { IStore } from '~/src/dash/reducers/store';
import {
  extractRouteParams,
  getRoutePath,
  navigate,
  parseLocation,
  RouteBillingDetailConfigurationExisting,
  RouteBillingDetailList,
} from '~/src/dash/router/routes';
import {
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
// utils
import { decorate } from '~/src/dash/utils/decorator';
import { routeName } from '~/src/dash/utils/routes';
// views
import { BillingDetailConfigurationSettingsForm } from '~/src/dash/views/billing-details/billing-detail-configuration-settings-form';
import { LoadingView } from '~/src/dash/views/loading-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

interface IState {
  billingDetailConfiguration?: IBillingDetailConfiguration;
  publishers: Array<Partial<IPublisher>>;
  snackbarMessage: string;
  snackbarError: boolean;
  loaded: boolean;
  isUpdating: boolean;
  refresh: number;
}

export const BillingDetailConfigurationSettingsView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      billingDetailConfiguration: undefined,
      publishers: [] as Array<Partial<IPublisher>>,
      snackbarMessage: '',
      snackbarError: false,
      loaded: false,
      isUpdating: false,
      refresh: 0,
    };

    componentDidMount() {
      this.getPublishers();
      this.getBillingDetailConfiguration();
    }

    componentDidUpdate(prevProps: Readonly<IProps>) {
      if (prevProps.location.pathname !== this.props.location.pathname) {
        this.getBillingDetailConfiguration();
      }
    }

    getPublishers = () => {
      getPublishersSimplifiedV1()
        .then((resp: any) => {
          const publishers = resp[0];
          if (publishers) {
            this.setState({
              publishers,
            });
          }
        })
        .catch(() => {
          this.setState({
            snackbarError: true,
            snackbarMessage: 'Error loading publishers',
          });
        });
    };

    getBillingDetailConfiguration(): void {
      const loc = parseLocation(this.props.location.pathname);

      if (loc.billingdetailconfiguration) {
        getBillingDetailConfiguration(loc.billingdetailconfiguration)
          .then((resp) => {
            this.setState({
              billingDetailConfiguration: resp[0],
              loaded: true,
            });
          })
          .catch(() => {
            this.setState(
              {
                snackbarMessage:
                  "You don't have access to this resource or it does not exist. Please create a new one or select one from the list.",
                snackbarError: true,
                loaded: false,
              },
              () => {
                setTimeout(() => {
                  navigate(
                    RouteBillingDetailList,
                    extractRouteParams({
                      session: this.props.session,
                      user: this.props.user,
                    }),
                  );
                }, 3000);
              },
            );
          });
      } else {
        this.setState({
          billingDetailConfiguration: {
            ID: 0,
            CreatedAt: '',
            UpdatedAt: '',
            DeletedAt: '',

            PublisherID: '',
            PublisherNameAlias: '',
            Product: '',
            IsTiered: EBool.EBool_FALSE,
            HasMinPrice: EBool.EBool_FALSE,
            MinPrice: 0,
            HasCustomCampaignPrice: EBool.EBool_FALSE,
            CustomCampaignPrice: 0,
            HasCustomVastPrice: EBool.EBool_FALSE,
            CustomVastPrice: 0,
          },
          loaded: true,
        });
      }
    }

    handleClose = () => {
      navigate(
        RouteBillingDetailList,
        extractRouteParams({
          session: this.props.session,
          user: this.props.user,
        }),
      );
    };

    // the form will emit a new billing-detail-tiers on change and re-render
    handleChange = (billingDetailConfiguration: IBillingDetailConfiguration) => {
      if (this.state.isUpdating) {
        return;
      }
      this.setState({
        billingDetailConfiguration,
      });
    };

    handleSubmit = (billingDetailConfiguration: IBillingDetailConfiguration) => {
      const { isUpdating } = this.state;

      if (!billingDetailConfiguration || isUpdating) {
        return;
      }
      this.setState(
        {
          isUpdating: true,
        },
        () => {
          if (billingDetailConfiguration.ID === 0) {
            createBillingDetailConfiguration(billingDetailConfiguration)
              .then((newBillingDetailConfiguration: IBillingDetailConfiguration) => {
                this.setState(
                  {
                    billingDetailConfiguration: newBillingDetailConfiguration,
                    snackbarError: false,
                    snackbarMessage: 'Successfully created billing detail configuration',
                    isUpdating: false,
                  },
                  this.getBillingDetailConfiguration,
                );
                navigate(RouteBillingDetailConfigurationExisting, extractRouteParams(this.props), {
                  billingdetailconfiguration: newBillingDetailConfiguration.ID.toString(),
                });
              })
              .catch((err) => {
                console.error(err);
                this.setState({
                  snackbarMessage: `Error while creating billing detail configuration. ${err}`,
                  snackbarError: true,
                  isUpdating: false,
                });
              });
          } else {
            // from this, we will always make the line item children the same active status as the order
            updateBillingDetailConfiguration(billingDetailConfiguration)
              .then((newBillingDetailConfiguration: IBillingDetailConfiguration) => {
                this.setState(
                  {
                    billingDetailConfiguration: newBillingDetailConfiguration,
                    snackbarError: false,
                    snackbarMessage: 'Successfully created billing detail configuration',
                    isUpdating: false,
                    refresh: this.state.refresh + 1,
                  },
                  () => {
                    this.getBillingDetailConfiguration();
                    window.location.href =
                      '/#' +
                      getRoutePath(
                        RouteBillingDetailConfigurationExisting,
                        extractRouteParams(this.props),
                        {
                          billingdetailconfiguration: newBillingDetailConfiguration.ID.toString(),
                        },
                      ) +
                      `&refresh=${this.state.refresh}`;
                  },
                );
              })
              .catch((err) => {
                console.error(err);
                this.setState({
                  snackbarMessage: `Error while updating billing detail configuration. ${err}`,
                  snackbarError: true,
                  isUpdating: false,
                });
              });
          }
        },
      );
    };

    handleCloseSnackbar = (): void => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    render() {
      const { classes, user, session, location } = this.props;
      const { pathname } = location;
      const {
        loaded,
        isUpdating,
        billingDetailConfiguration,
        publishers,
        snackbarMessage,
        snackbarError,
      } = this.state;
      if (loaded) {
        return (
          <Panels maxWidth vertical relative minWidthMain>
            <Panel largeHorizontalPadding verticalPadding>
              <ModelTopBar
                title={routeName(pathname)}
                session={session}
                user={user}
                publisher={session.publisher}
                classes={classes}
                delegateElevate={true}
                currentPath={pathname}
                disableTitlePaddingLeft
              />
            </Panel>
            <BillingDetailConfigurationSettingsForm
              billingDetailConfiguration={billingDetailConfiguration}
              publishers={publishers}
              onChange={this.handleChange}
              onSubmit={this.handleSubmit}
              onClose={this.handleClose}
              isUpdating={isUpdating}
            />
            <SnackbarView
              open={!!snackbarMessage}
              error={!!snackbarError}
              onClose={this.handleCloseSnackbar}
            >
              {snackbarMessage}
            </SnackbarView>
          </Panels>
        );
      } else {
        return <LoadingView active={true} text='Loading the billing details configuration' />;
      }
    }
  },
);
